import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, CheckCircle, Package, Download, Plus } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { useAuth } from '../contexts/auth'

type Location = { id: string; code: string; type: string; zone: string }
type InboundItem = {
  id: string; productId: string; quantityExpected: number
  quantityReceived: number | null; status: string
  productName?: string | null; productSku?: string | null; unitsPerBox?: number | null
  product?: { sku: string; name: string }
}

// Nome amigável do produto (nome — SKU); cai no UUID curto só se faltar tudo
function itemLabel(i: { productName?: string | null; productSku?: string | null; product?: { sku: string; name: string }; productId: string }) {
  const name = i.productName ?? i.product?.name
  const sku = i.productSku ?? i.product?.sku
  if (name && sku) return `${name} — ${sku}`
  return name || sku || `${i.productId.slice(0, 8)}…`
}
type InboundOrder = {
  id: string; orderNumber: string; nfNumber: string | null; nfValue: string | null
  status: string; notes: string | null; createdAt: string; tenantId: string
  items: InboundItem[]
}
type Pallet = {
  id: string; code: string; type: string; status: string
  locationCode: string | null; quantityBoxes: number; quantityUnits: number
  expiryDate: string | null; createdAt: string
}
type ReceiveRow = { itemId: string; locationId: string; quantityReceived: number; lotNumber: string }
type PalletItemRow = { productId: string; quantityUnits: number; quantityBoxes: number }

const statusLabel: Record<string, string> = { pending: 'Pendente', receiving: 'Recebendo', received: 'Recebido', divergent: 'Divergente' }
const statusVariant: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  pending: 'default', receiving: 'info', received: 'success', divergent: 'warning',
}

/** Mostra o que o servidor respondeu, seja qual for o formato. A mensagem
 *  generica escondia a causa real — no 400 do recebimento sem lote, por exemplo,
 *  o motivo vinha no corpo e ninguem via. */
function msgErro(e: unknown, padrao: string): string {
  const r = (e as { response?: { status?: number; data?: unknown }; message?: string })?.response
  const d = r?.data as Record<string, unknown> | string | undefined
  if (typeof d === 'string' && d.trim()) return d
  if (d && typeof d === 'object') {
    const m = (d.detail ?? d.title ?? d.message) as string | undefined
    if (m) return r?.status ? `${m} (HTTP ${r.status})` : m
    return JSON.stringify(d)
  }
  return (e as { message?: string })?.message ?? padrao
}

const emptyPalletForm = { prefix: '', type: 'pallet' as 'pallet' | 'box', locationId: '', quantityBoxes: 0, quantityUnits: 0, expiryDate: '', notes: '' }

export function InboundDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user } = useAuth()

  const [receiveModal, setReceiveModal] = useState(false)
  const [palletModal, setPalletModal] = useState(false)
  const [rows, setRows] = useState<ReceiveRow[]>([])
  const [allowDiv, setAllowDiv] = useState(false)
  const [palletForm, setPalletForm] = useState(emptyPalletForm)
  const [palletItems, setPalletItems] = useState<PalletItemRow[]>([])
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [labelFormat, setLabelFormat] = useState<'10x15' | 'a4'>('10x15')

  const { data: order, isLoading } = useQuery<InboundOrder>({
    queryKey: ['inbound', id],
    queryFn: async () => (await api.get(`/inbound/${id}`)).data,
  })

  const { data: locations = [] } = useQuery<Location[]>({
    queryKey: ['locations'],
    queryFn: async () => (await api.get('/stock/locations')).data,
  })

  const { data: pallets = [], refetch: refetchPallets } = useQuery<Pallet[]>({
    queryKey: ['pallets', 'inbound', id],
    queryFn: async () => (await api.get('/pallets', { params: { tenantId: order?.tenantId, inboundOrderId: id } })).data,
    enabled: !!order?.tenantId,
  })

  const openReceive = () => {
    if (!order) return
    setRows(order.items.map(i => ({ itemId: i.id, locationId: '', quantityReceived: i.quantityExpected, lotNumber: '' })))
    setReceiveModal(true)
  }

  const openPalletModal = () => {
    if (!order) return
    setPalletForm({ ...emptyPalletForm, prefix: order.orderNumber.replace(/[^A-Za-z0-9]/g, '').toUpperCase() })
    setPalletItems(order.items.filter(i => (i.quantityReceived ?? 0) > 0).map(i => ({
      productId: i.productId,
      quantityUnits: i.quantityReceived ?? 0,
      quantityBoxes: 0,
    })))
    setPalletModal(true)
  }

  const receive = useMutation({
    mutationFn: async () =>
      api.post(`/inbound/${id}/receive`, {
        // lote vazio nao vai como string vazia: o servidor entende ausencia,
        // nao ''.
        items: rows.filter(r => r.locationId).map(r => ({
          itemId: r.itemId, locationId: r.locationId, quantityReceived: r.quantityReceived,
          ...(r.lotNumber.trim() ? { lotNumber: r.lotNumber.trim() } : {}),
        })),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbound', id] })
      setReceiveModal(false)
    },
  })

  // Desfaz o recebimento inteiro para poder refazer — caso típico: conferir a
  // mercadoria antes de cadastrar "unidades por caixa" no produto.
  const [estorno, setEstorno] = useState<{ previa: any | null; erro: string; rodando: boolean }>(
    { previa: null, erro: '', rodando: false })

  async function estornar(confirmar: boolean) {
    setEstorno(p => ({ ...p, rodando: true, erro: '' }))
    try {
      const { data } = await api.post(`/inbound/${id}/estornar`, { confirmar })
      setEstorno({ previa: data, erro: '', rodando: false })
      if (confirmar) qc.invalidateQueries({ queryKey: ['inbound', id] })
    } catch (e: any) {
      setEstorno(p => ({ ...p, rodando: false, erro: e?.response?.data?.title ?? 'Falha ao estornar' }))
    }
  }

  const confirm = useMutation({
    mutationFn: async () => api.post(`/inbound/${id}/confirm`, { allowDivergence: allowDiv }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbound', id] }),
  })

  const createPallet = useMutation({
    mutationFn: async () => api.post('/pallets', {
      tenantId: order!.tenantId,
      inboundOrderId: id,
      prefix: palletForm.prefix,
      type: palletForm.type,
      locationId: palletForm.locationId || undefined,
      quantityBoxes: totalCaixas,
      quantityUnits: totalUnidades,
      expiryDate: palletForm.expiryDate || undefined,
      notes: palletForm.notes || undefined,
      items: palletItems.filter(i => i.productId && (i.quantityUnits > 0 || i.quantityBoxes > 0)),
    }),
    onSuccess: () => {
      refetchPallets()
      setPalletModal(false)
      setPalletForm(emptyPalletForm)
    },
  })

  async function downloadLabel(palletId: string, code: string) {
    setDownloadingId(palletId)
    try {
      const res = await api.get(`/pallets/${palletId}/label`, {
        params: { format: labelFormat },
        responseType: 'blob',
      })
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `etiqueta-${code}-${labelFormat}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setDownloadingId(null)
    }
  }

  const setRow = (i: number, k: keyof ReceiveRow, v: string | number) =>
    setRows(p => p.map((r, idx) => idx === i ? { ...r, [k]: v } : r))

  const setPalletItem = (idx: number, patch: Partial<PalletItemRow>) =>
    setPalletItems(p => p.map((r, i) => i === idx ? { ...r, ...patch } : r))

  if (isLoading) return <p className="p-8 text-center text-sm text-gray-400">Carregando...</p>
  if (!order) return <p className="p-8 text-center text-sm text-gray-400">Ordem não encontrada.</p>

  // Totais do palete = soma dos itens (não são mais digitados).
  const totalCaixas = palletItems.reduce((acc, i) => acc + (i.quantityBoxes || 0), 0)
  const totalUnidades = palletItems.reduce((acc, i) => acc + (i.quantityUnits || 0), 0)
  const upcDoProduto = (productId: string) =>
    order.items.find(i => i.productId === productId)?.unitsPerBox ?? 0
  const upcDoPalete = palletItems.length === 1 ? upcDoProduto(palletItems[0].productId) : 0

  // "Registrar recebimento" some quando não falta nada: ele SOMA ao recebido, e
  // clicar por engano com tudo recebido duplicaria a entrada no estoque.
  const faltaReceber = order.items.some(i => (i.quantityReceived ?? 0) < i.quantityExpected)
  const canReceive = ['pending', 'receiving'].includes(order.status) && faltaReceber
  const canConfirm = order.status === 'receiving'
  // Só admin, e só faz sentido quando já houve entrada — ordem pendente não tem
  // o que desfazer.
  const podeEstornar = user?.role === 'admin' && ['receiving', 'received', 'divergent'].includes(order.status)
  const hasReceived = order.items.some(i => (i.quantityReceived ?? 0) > 0)

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/inbound')}><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Recebimento — {order.orderNumber}</h2>
          <p className="text-sm text-gray-500">Criado em {new Date(order.createdAt).toLocaleString('pt-BR')}</p>
        </div>
        <Badge variant={statusVariant[order.status] ?? 'default'}>{statusLabel[order.status] ?? order.status}</Badge>
      </div>

      <Card>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 text-sm">
            <div><p className="text-gray-500">NF</p><p className="font-medium">{order.nfNumber ?? '—'}</p></div>
            <div><p className="text-gray-500">Valor NF</p><p className="font-medium">{order.nfValue ? `R$ ${parseFloat(order.nfValue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '—'}</p></div>
            <div><p className="text-gray-500">Obs.</p><p className="font-medium">{order.notes ?? '—'}</p></div>
          </div>
        </CardContent>
      </Card>

      {/* Itens */}
      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">Itens</span>
          <div className="flex gap-2">
            {canReceive && <Button size="sm" onClick={openReceive}>Registrar recebimento</Button>}
            {canConfirm && (
              <Button variant="secondary" size="sm" loading={confirm.isPending} onClick={() => confirm.mutate()}>
                <CheckCircle className="h-4 w-4" /> Confirmar
              </Button>
            )}
            {podeEstornar && (
              <Button variant="ghost" size="sm" loading={estorno.rodando} onClick={() => estornar(false)}
                title="Desfaz este recebimento (estorna o estoque e apaga os paletes) para poder receber de novo">
                Refazer recebimento
              </Button>
            )}
          </div>
        </CardHeader>

        {(estorno.previa || estorno.erro) && (
          <div className="border-b border-gray-100 px-5 py-4">
            {estorno.erro && <p className="text-sm text-red-600">{estorno.erro}</p>}

            {estorno.previa && !estorno.previa.confirmado && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-amber-800">
                  Isto vai desfazer o recebimento — confira antes:
                </p>
                <ul className="ml-4 list-disc space-y-0.5 text-sm text-gray-600">
                  <li>{estorno.previa.lancamentos} lançamento(s) de entrada serão estornados
                      ({estorno.previa.unidades} unidade(s) saem do saldo)</li>
                  <li>{estorno.previa.paletes.length} palete(s) formados neste recebimento serão apagados
                      {estorno.previa.paletes.length > 0 && `: ${estorno.previa.paletes.map((x: any) => x.code).join(', ')}`}</li>
                  <li>as quantidades recebidas voltam a zero e a ordem volta para <b>Pendente</b></li>
                </ul>
                {estorno.previa.itens?.length > 0 && (
                  <div className="mt-2 rounded-lg border border-gray-100">
                    {estorno.previa.itens.map((it: any, i: number) => (
                      <div key={i} className="flex gap-3 border-b border-gray-100 px-3 py-1.5 text-xs last:border-0">
                        <span className="font-mono text-gray-500">{it.sku ?? '—'}</span>
                        <span className="flex-1 truncate text-gray-700">{it.produto ?? '—'}</span>
                        <span className="font-semibold text-gray-900">{it.quantidade}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 pt-1">
                  <Button variant="secondary" size="sm" onClick={() => setEstorno({ previa: null, erro: '', rodando: false })}>
                    Cancelar
                  </Button>
                  <Button size="sm" loading={estorno.rodando} onClick={() => estornar(true)}>
                    Confirmar estorno
                  </Button>
                </div>
              </div>
            )}

            {estorno.previa?.confirmado && (
              <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                <strong>Recebimento desfeito.</strong> {estorno.previa.estornados} lançamento(s) estornado(s)
                {estorno.previa.palettesRemovidos > 0 && `, ${estorno.previa.palettesRemovidos} palete(s) removido(s)`}.
                A ordem voltou para Pendente — pode registrar o recebimento de novo.
              </div>
            )}
          </div>
        )}
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100 bg-gray-50">
              <tr>
                <th className="px-5 py-3 text-left font-medium text-gray-600">Produto</th>
                <th className="px-5 py-3 text-right font-medium text-gray-600">Esperado</th>
                <th className="px-5 py-3 text-right font-medium text-gray-600">Recebido</th>
                <th className="px-5 py-3 text-left font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {order.items.map((item) => {
                const recv = item.quantityReceived ?? 0
                const diff = recv - item.quantityExpected
                const st = recv === 0 ? 'pending' : diff === 0 ? 'received' : 'divergent'
                return (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 text-gray-800">{itemLabel(item)}</td>
                    <td className="px-5 py-3 text-right">{item.quantityExpected}</td>
                    <td className={`px-5 py-3 text-right font-semibold ${diff < 0 ? 'text-orange-600' : diff > 0 ? 'text-blue-600' : 'text-gray-900'}`}>
                      {item.quantityReceived ?? '—'}
                    </td>
                    <td className="px-5 py-3">
                      <Badge variant={st === 'received' ? 'success' : st === 'divergent' ? 'warning' : 'default'}>
                        {statusLabel[st] ?? st}
                      </Badge>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Paletes desta ordem */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-900">Paletes / Caixas</span>
            <span className="text-xs text-gray-400">({pallets.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <select className="rounded-lg border border-gray-200 px-2 py-1 text-xs"
              value={labelFormat} onChange={e => setLabelFormat(e.target.value as '10x15' | 'a4')}>
              <option value="10x15">10×15cm</option>
              <option value="a4">A4</option>
            </select>
            {hasReceived && (
              <Button size="sm" onClick={openPalletModal}>
                <Plus className="h-4 w-4" /> Criar palete
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {pallets.length === 0 ? (
            <p className="px-5 py-6 text-center text-sm text-gray-400">
              {hasReceived ? 'Nenhum palete criado ainda — clique em "Criar palete"' : 'Registre o recebimento antes de criar paletes'}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Código</th>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Tipo</th>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Local</th>
                  <th className="px-5 py-3 text-right font-medium text-gray-600">Cx</th>
                  <th className="px-5 py-3 text-right font-medium text-gray-600">Un</th>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Validade</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pallets.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-mono font-bold text-gray-900">{p.code}</td>
                    <td className="px-5 py-3 text-gray-600">{p.type === 'box' ? 'Caixa' : 'Palete'}</td>
                    <td className="px-5 py-3 font-mono text-gray-700">{p.locationCode ?? '—'}</td>
                    <td className="px-5 py-3 text-right">{p.quantityBoxes || '—'}</td>
                    <td className="px-5 py-3 text-right">{p.quantityUnits || '—'}</td>
                    <td className="px-5 py-3 text-gray-600">{p.expiryDate ?? '—'}</td>
                    <td className="px-5 py-3">
                      <Button variant="ghost" size="sm" loading={downloadingId === p.id}
                        onClick={() => downloadLabel(p.id, p.code)}>
                        <Download className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Modal recebimento */}
      <Modal title="Registrar recebimento" open={receiveModal} onClose={() => setReceiveModal(false)} size="lg">
        <div className="space-y-4">
          <p className="text-sm text-gray-500">Informe a localização, lote e quantidade recebida para cada item:</p>
          <div className="space-y-3">
            {rows.map((row, i) => {
              const item = order.items[i]
              return (
                <div key={row.itemId} className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-2">
                  <p className="text-xs text-gray-700">{itemLabel(item)} — esperado: {item.quantityExpected}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-700">Localização *</label>
                      <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" value={row.locationId} onChange={e => setRow(i, 'locationId', e.target.value)}>
                        <option value="">Selecione...</option>
                        {locations.map(l => <option key={l.id} value={l.id}>{l.code} ({l.type})</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-700">Número do lote</label>
                      <input className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono"
                        placeholder="Ex: LOT-2026-001"
                        value={row.lotNumber} onChange={e => setRow(i, 'lotNumber', e.target.value)} />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-700">Qtd recebida</label>
                      <input type="number" min={0} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-right"
                        value={row.quantityReceived} onChange={e => setRow(i, 'quantityReceived', parseInt(e.target.value) || 0)} />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          {receive.error && (
            <p className="break-words text-sm text-red-600">{msgErro(receive.error, 'Erro ao registrar')}</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setReceiveModal(false)}>Cancelar</Button>
            <Button loading={receive.isPending} disabled={rows.every(r => !r.locationId)} onClick={() => receive.mutate()}>
              Confirmar recebimento
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal criar palete */}
      <Modal title="Criar palete / caixa" open={palletModal} onClose={() => setPalletModal(false)} size="lg">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Prefixo do código *</label>
              <input className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono uppercase"
                value={palletForm.prefix}
                onChange={e => setPalletForm(p => ({ ...p, prefix: e.target.value.toUpperCase() }))}
                placeholder="Ex: NF001" />
              <p className="mt-1 text-xs text-gray-400">Código gerado: {palletForm.prefix}-P{String(pallets.length + 1).padStart(2, '0')}</p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Tipo</label>
              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={palletForm.type} onChange={e => setPalletForm(p => ({ ...p, type: e.target.value as 'pallet' | 'box' }))}>
                <option value="pallet">Palete</option>
                <option value="box">Caixa fechada</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Localização</label>
              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={palletForm.locationId} onChange={e => setPalletForm(p => ({ ...p, locationId: e.target.value }))}>
                <option value="">Sem localização</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.code}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Validade</label>
              <input type="date" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={palletForm.expiryDate}
                onChange={e => setPalletForm(p => ({ ...p, expiryDate: e.target.value }))} />
            </div>
          </div>

          {/* Os totais NÃO são digitados: somam os itens. Antes o mesmo número
              era pedido duas vezes (aqui e no item), e as duas versões podiam
              divergir — foi assim que um palete nasceu com a conta errada. */}
          <div className="flex gap-6 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            <div>
              <span className="block text-xs text-gray-500">Total de caixas</span>
              <span className="text-lg font-semibold text-gray-900">{totalCaixas}</span>
            </div>
            <div>
              <span className="block text-xs text-gray-500">Total de unidades</span>
              <span className="text-lg font-semibold text-gray-900">{totalUnidades}</span>
            </div>
            {upcDoPalete > 0 && (
              <div className="ml-auto self-center text-xs text-gray-500">
                {upcDoPalete} un/cx no cadastro
              </div>
            )}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Itens no palete</label>
            <div className="space-y-2">
              {palletItems.map((item, idx) => (
                <div key={idx} className="grid grid-cols-3 gap-2 items-center rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                  <p className="text-xs text-gray-700 truncate">{itemLabel(order.items.find(o => o.productId === item.productId) ?? item)}</p>
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Caixas</label>
                    <input type="number" min={0} className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-right"
                      value={item.quantityBoxes}
                      onChange={e => {
                        const cx = parseInt(e.target.value) || 0
                        const upc = upcDoProduto(item.productId)
                        // Com "un/cx" no cadastro, a unidade é consequência da
                        // caixa — digitar os dois é onde a conta se perde.
                        setPalletItem(idx, upc > 0 ? { quantityBoxes: cx, quantityUnits: cx * upc } : { quantityBoxes: cx })
                      }} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">
                      Unidades
                      {upcDoProduto(item.productId) > 0 && <span className="ml-1 text-gray-400">(calculado)</span>}
                    </label>
                    <input type="number" min={0} className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-right"
                      value={item.quantityUnits}
                      onChange={e => setPalletItem(idx, { quantityUnits: parseInt(e.target.value) || 0 })} />
                    {upcDoProduto(item.productId) === 0 && (
                      <p className="mt-0.5 text-[11px] text-amber-600">
                        produto sem "un/cx" no cadastro — o abastecimento não vai conseguir converter
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {createPallet.error && <p className="text-sm text-red-600">{(createPallet.error as any)?.response?.data?.detail ?? (createPallet.error as any)?.response?.data?.title ?? 'Erro ao criar palete'}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setPalletModal(false)}>Cancelar</Button>
            <Button loading={createPallet.isPending} disabled={!palletForm.prefix} onClick={() => createPallet.mutate()}>
              Criar palete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirmar com divergência */}
      {canConfirm && (
        <div className="flex items-center gap-3 rounded-lg border border-orange-100 bg-orange-50 px-4 py-3 text-sm">
          <input type="checkbox" id="allowDiv" checked={allowDiv} onChange={e => setAllowDiv(e.target.checked)} className="h-4 w-4" />
          <label htmlFor="allowDiv" className="text-orange-800 cursor-pointer">
            Aceitar divergências (quantidades diferentes do esperado) ao confirmar
          </label>
        </div>
      )}
    </div>
  )
}
